import { AlertCircle, Bell, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card } from "@/components/ui/card";
import { EmptyStateWithError, QueryErrorDisplay } from "@/lib/components/errors";
import { getUserFriendlyErrorMessage } from "@/lib/error/error-utils";
import { cn } from "@/lib/utils";
import { useNotifications } from "../../hooks";
import { NotificationCardMobile } from "../cards/notification-card-mobile";

interface NotificationListMobileProps {
  className?: string;
  onOpenDialog?: (type: string, id: string, params?: Record<string, any>) => void;
}

export function NotificationListMobile({ className, onOpenDialog }: NotificationListMobileProps) {
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Main hook
  const {
    notifications,
    loading,
    isLoadingMore,
    error,
    hasMore,
    retry,
    loadMore,
    refetch,
    markAsRead,
  } = useNotifications();

  const handleMarkAsRead = useCallback(
    async (ids: string[], markAll = false) => {
      await markAsRead(ids, markAll);
    },
    [markAsRead]
  );

  // Pull to refresh implementation
  const handlePullToRefresh = useCallback(async () => {
    if (isRefreshing || loading) return;

    setIsRefreshing(true);
    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, loading, refetch]);

  // Intersection observer for infinite scroll
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && hasMore && !loading && !isLoadingMore) {
          if (timeoutId) {
            clearTimeout(timeoutId);
          }

          timeoutId = setTimeout(() => {
            loadMore();
          }, 200);
        }
      },
      {
        threshold: 0.1,
        rootMargin: "100px",
      }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      observer.disconnect();
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [hasMore, loading, isLoadingMore, loadMore]);

  // Loading state - show spinner for initial load
  if (loading && notifications.length === 0) {
    return (
      <div className="flex justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Error state - show error display for initial load failures
  if (error && notifications.length === 0) {
    return <QueryErrorDisplay error={error} onRetry={retry} className={className} />;
  }

  return (
    <div className={cn("relative", className)}>
      {/* Pull to refresh indicator */}
      {isRefreshing && (
        <div className="absolute top-0 left-0 right-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border p-2">
          <div className="flex items-center justify-center gap-2">
            <RefreshCw className="h-4 w-4 animate-spin text-primary" />
            <span className="text-sm text-muted-foreground">Refreshing...</span>
          </div>
        </div>
      )}

      {/* Error alert for ongoing operations */}
      {error && notifications.length > 0 && (
        <Alert variant="destructive" className="mx-3 mb-3">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{getUserFriendlyErrorMessage(error)}</AlertDescription>
        </Alert>
      )}

      {/* Notifications List */}
      {notifications.length === 0 ? (
        <EmptyStateWithError
          title="No notifications"
          description="You're all caught up!"
          error={null}
          onRetry={retry}
          actionButton={null}
          icon={<Bell className="h-12 w-12 text-muted-foreground" />}
        />
      ) : (
        <div ref={listRef} className="min-h-0">
          {/* Pull to refresh area */}
          <div
            className="pt-3"
            onTouchStart={(e) => {
              const touch = e.touches[0];
              const startY = touch.clientY;
              const listElement = listRef.current;

              if (listElement && listElement.scrollTop === 0) {
                // At top of list, enable pull to refresh
                const handleTouchMove = (moveEvent: TouchEvent) => {
                  const currentTouch = moveEvent.touches[0];
                  const deltaY = currentTouch.clientY - startY;

                  if (deltaY > 50 && !isRefreshing) {
                    handlePullToRefresh();
                    document.removeEventListener("touchmove", handleTouchMove);
                  }
                };

                document.addEventListener("touchmove", handleTouchMove, {
                  passive: true,
                });
                document.addEventListener(
                  "touchend",
                  () => {
                    document.removeEventListener("touchmove", handleTouchMove);
                  },
                  { once: true }
                );
              }
            }}
          />

          {/* Notification cards */}
          <Card className="mx-3 mb-3">
            <div className="divide-y divide-border">
              {notifications.map((notification) => (
                <NotificationCardMobile
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={(id) => handleMarkAsRead([id])}
                  onOpenDialog={onOpenDialog}
                />
              ))}
            </div>
          </Card>

          {/* Load more trigger */}
          {hasMore && (
            <div ref={loadMoreRef} className="flex justify-center py-4 min-h-[60px]">
              {isLoadingMore ? (
                <div className="flex items-center gap-2">
                  <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Loading more...</span>
                </div>
              ) : (
                <div className="h-4 w-4" />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
